import React, {Component} from 'react';
import TextInput from "../../components/text-input/TextInput";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Select from "react-select";
import StringUtil from "../../utils/StringUtil";
import IntakeFormTextEnum from "./intakeform/enum/IntakeFormTextEnum";

/**
 * Allergy section component for intake forms that uses the correct DTO structure.
 * 
 * This stores allergies in the format expected by the backend:
 * { items: [{ allergyType, allergen, otherAllergen, activeIngredient, allergenSeverity, details, ... }, ...] }
 * 
 * Props:
 * - allergy: The allergy object { items: [...] }
 * - readOnly: Whether the form is read-only
 * - selectedLanguage: Language for labels ("English" | "Arabic")
 * - allergyTypes: Lookup array from Redux state
 * - allergenTypes: Lookup array from Redux state
 * - allergySeverities: Lookup array from Redux state
 * - allergenReactionTypes: Lookup array from Redux state
 */
class FormAllergiesSection extends Component {

    constructor(props) {
        super(props);
        this.state = {
            // Input fields for new allergy entry
            allergyType: null,
            allergen: null,
            otherAllergen: '',
            medication: '',
            allergenSeverity: null,
            allergenReaction: null,
            otherAllergenReaction: ''
        };
    }

    initializeAllergyItems = () => {
        if (!this.props.allergy.items) {
            this.props.allergy.items = [];
        }
    };

    isOtherOption = (option) => {
        return option != null &&
            ("OTHER" === option.name?.toUpperCase() || "OTHERS" === option.name?.toUpperCase());
    };

    isDrugAllergy = (allergyType) => {
        return allergyType != null && allergyType.name?.toUpperCase().includes("DRUG");
    };

    addAllergy = () => {
        const {allergyType, allergen, otherAllergen, medication, allergenSeverity, allergenReaction, otherAllergenReaction} = this.state;

        if (!allergyType) {
            return;
        }

        const isDrug = this.isDrugAllergy(allergyType);
        if (isDrug && StringUtil.isNullOrEmpty(medication)) {
            return;
        }
        if (!isDrug && !allergen) {
            return;
        }

        this.initializeAllergyItems();

        this.props.allergy.items.push({
            allergyType: allergyType,
            allergen: allergen,
            otherAllergen: this.isOtherOption(allergen) ? otherAllergen : null,
            activeIngredient: medication || null,
            allergenSeverity: allergenSeverity,
            allergenReaction: allergenReaction,
            otherAllergenReaction: this.isOtherOption(allergenReaction) ? otherAllergenReaction : null
        });

        // Clear input fields
        this.setState({
            allergyType: null,
            allergen: null,
            otherAllergen: '',
            medication: '',
            allergenSeverity: null,
            allergenReaction: null,
            otherAllergenReaction: ''
        });
    };

    removeAllergy = (index) => {
        if (this.props.allergy.items) {
            this.props.allergy.items.splice(index, 1);
            this.forceUpdate();
        }
    };

    getAllergyDivs = () => {
        const {allergy, readOnly} = this.props;
        
        if (!allergy || !allergy.items || allergy.items.length === 0) {
            return null;
        }

        return allergy.items.map((item, index) => {
            const displayParts = [];
            
            if (item.allergyType?.name) {
                displayParts.push(item.allergyType.name);
            }
            if (item.allergen?.name) {
                displayParts.push(item.allergen.name);
            }
            if (item.otherAllergen) {
                displayParts.push(item.otherAllergen);
            }
            if (item.activeIngredient) {
                displayParts.push(`Medication: ${item.activeIngredient}`);
            }
            if (item.allergenSeverity?.name) {
                displayParts.push(`Severity: ${item.allergenSeverity.name}`);
            }
            if (item.allergenReaction?.name) {
                displayParts.push(`Reaction: ${item.allergenReaction.name}`);
            }
            if (item.otherAllergenReaction) {
                displayParts.push(`Reaction: ${item.otherAllergenReaction}`);
            }

            return (
                <div className={"div-list-item"} key={`allergy-${index}`}>
                    <div>{displayParts.join(' - ')}</div>
                    {!readOnly && (
                        <Button 
                            className={"emr-transparent-delete-button"}
                            variant="warning"
                            style={{fontSize: "10px", marginLeft: "2px"}}
                            onClick={() => this.removeAllergy(index)}
                        >
                            <FontAwesomeIcon icon={["fas", "times"]}/>
                        </Button>
                    )}
                </div>
            );
        });
    };

    render() {
        const {selectedLanguage, readOnly, allergyTypes, allergenTypes, allergySeverities, allergenReactionTypes} = this.props;
        const {allergyType, allergen, otherAllergen, medication, allergenSeverity, allergenReaction, otherAllergenReaction} = this.state;

        const isDrugAllergySelected = this.isDrugAllergy(allergyType);
        const showAllergenType = allergyType && !isDrugAllergySelected;
        const showMedication = isDrugAllergySelected;

        let addButtonDisabled = !allergyType;
        if (!addButtonDisabled) {
            if (isDrugAllergySelected) {
                addButtonDisabled = StringUtil.isNullOrEmpty(medication);
            } else {
                addButtonDisabled = !allergen;
            }
        }

        const allergyDivs = this.getAllergyDivs();
        const showOtherAllergen = showAllergenType && this.isOtherOption(allergen);

        return (
            <div className={"container"}>
                {/* Row 1: Allergy Type & Allergen Type (conditionally shown) */}
                <div className={"row mx-0 mt-0 mb-2"}>
                    <div className={showAllergenType ? "col-md-6" : "col-md-12"}>
                        <div style={{fontSize: "12px", fontWeight: "bold", marginBottom: "4px"}}>
                            {IntakeFormTextEnum.Allergy_Type?.[selectedLanguage] || "Allergy Type"} *
                        </div>
                        <Select
                            className={"emr-white-select-container"}
                            classNamePrefix={"emr-white-select"}
                            placeholder={IntakeFormTextEnum.Select_Type?.[selectedLanguage] || "Select Type"}
                            value={allergyType}
                            isDisabled={readOnly}
                            isClearable
                            getOptionValue={v => v.id}
                            getOptionLabel={v => v.name}
                            options={allergyTypes || []}
                            onChange={(value) => this.setState({
                                allergyType: value,
                                allergen: null,
                                otherAllergen: '',
                                medication: ''
                            })}
                        />
                    </div>
                    {showAllergenType && (
                        <div className={"col-md-6"}>
                            <div style={{fontSize: "12px", fontWeight: "bold", marginBottom: "4px"}}>
                                {IntakeFormTextEnum.Allergen?.[selectedLanguage] || "Allergen Type"} *
                            </div>
                            <Select
                                className={"emr-white-select-container"}
                                classNamePrefix={"emr-white-select"}
                                placeholder={IntakeFormTextEnum.Select_Type?.[selectedLanguage] || "Select Type"}
                                value={allergen}
                                isDisabled={readOnly}
                                isClearable
                                getOptionValue={v => v.id}
                                getOptionLabel={v => v.name}
                                options={allergenTypes || []}
                                onChange={(value) => this.setState({allergen: value, otherAllergen: ''})}
                            />
                        </div>
                    )}
                </div>

                {/* Other Allergen (if Other selected) */}
                {showOtherAllergen && (
                    <div className={"row mx-0 mb-2"}>
                        <div className={"col-md-6"}></div>
                        <div className={"col-md-6"}>
                            <TextInput
                                value={otherAllergen}
                                placeholder={IntakeFormTextEnum.Other_Allergen?.[selectedLanguage] || "Specify Allergen"}
                                disabled={readOnly}
                                onChange={value => this.setState({otherAllergen: value})}
                                style={{fontSize: "14px", width: "100%"}}
                            />
                        </div>
                    </div>
                )}

                {/* Medication (only shown for Drug Allergy) */}
                {showMedication && (
                    <div className={"row mx-0 mb-2"}>
                        <div className={"col-md-12"}>
                            <div style={{fontSize: "12px", fontWeight: "bold", marginBottom: "4px"}}>
                                {IntakeFormTextEnum.Medication_Allergy_Name?.[selectedLanguage] || "Medication"} *
                            </div>
                            <TextInput
                                value={medication}
                                placeholder={IntakeFormTextEnum.Medication_Allergy_Name?.[selectedLanguage] || "Medication Name"}
                                disabled={readOnly}
                                onChange={value => this.setState({medication: value})}
                                style={{fontSize: "14px", width: "100%"}}
                            />
                        </div>
                    </div>
                )}

                {/* Severity & Reaction Type */}
                <div className={"row mx-0 mb-2"}>
                    <div className={"col-md-6"}>
                        <div style={{fontSize: "12px", fontWeight: "bold", marginBottom: "4px"}}>
                            {IntakeFormTextEnum.Severity?.[selectedLanguage] || "Severity"}
                        </div>
                        <Select
                            className={"emr-white-select-container"}
                            classNamePrefix={"emr-white-select"}
                            placeholder={IntakeFormTextEnum.Select_Type?.[selectedLanguage] || "Select Severity"}
                            value={allergenSeverity}
                            isDisabled={readOnly}
                            isClearable
                            getOptionValue={v => v.id}
                            getOptionLabel={v => v.name}
                            options={allergySeverities || []}
                            onChange={(value) => this.setState({allergenSeverity: value})}
                        />
                    </div>
                    <div className={"col-md-6"}>
                        <div style={{fontSize: "12px", fontWeight: "bold", marginBottom: "4px"}}>
                            {IntakeFormTextEnum.Reaction_Type?.[selectedLanguage] || "Reaction Type"}
                        </div>
                        <Select
                            className={"emr-white-select-container"}
                            classNamePrefix={"emr-white-select"}
                            placeholder={IntakeFormTextEnum.Select_Type?.[selectedLanguage] || "Select Reaction Type"}
                            value={allergenReaction}
                            isDisabled={readOnly}
                            isClearable
                            getOptionValue={v => v.id}
                            getOptionLabel={v => v.name}
                            options={allergenReactionTypes || []}
                            onChange={(value) => this.setState({allergenReaction: value, otherAllergenReaction: ''})}
                        />
                    </div>
                </div>

                {/* Other Reaction Type (if Other selected) */}
                {this.isOtherOption(allergenReaction) && (
                    <div className={"row mx-0 mb-2"}>
                        <div className={"col-md-6"}></div>
                        <div className={"col-md-6"}>
                            <TextInput
                                value={otherAllergenReaction}
                                placeholder={IntakeFormTextEnum.Other_Reaction_Type?.[selectedLanguage] || "Specify Reaction Type"}
                                disabled={readOnly}
                                onChange={value => this.setState({otherAllergenReaction: value})}
                                style={{fontSize: "14px", width: "100%"}}
                            />
                        </div>
                    </div>
                )}

                {/* Add Button */}
                <div className={"row mx-0 mb-2"}>
                    <div className={"col-md-10"}></div>
                    <div className={"col-md-2 d-flex align-items-end justify-content-end"}>
                        {!readOnly && (
                            <Button
                                className={"emr-plus-button"}
                                variant="success"
                                disabled={addButtonDisabled}
                                style={{fontSize: "12px"}}
                                onClick={this.addAllergy}
                            >
                                <FontAwesomeIcon icon={["fas", "plus"]}/>
                            </Button>
                        )}
                    </div>
                </div>

                {/* Display added allergies */}
                <div className={"row no-margin mt-2"}>
                    <div className={"div-list-wrapper"}>
                        {allergyDivs}
                    </div>
                </div>
            </div>
        );
    }
}

export default FormAllergiesSection;
